import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useForm, useFormContext} from "react-hook-form";
import findIndex from "lodash/findIndex";
import {useDispatch} from "react-redux";

import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { Check as CheckIcon } from '@vedmarket/ui-kit';
import Link from "@mui/material/Link";

import { useUpdateProductById } from "../../../../../../../../../../../../../api/calc";
import {checkFloat} from "../../../../../../../../../../../../../utils/validation";
import AlertBottom from '../../../../../../../../../../../../common/alertBottom';

import {getVehicleFields, isVehicleHsCode, PACKAGE_FORM, PackageForm} from "../add_form/helpers";
import VehicleFields from "../vehicle_fields";

import './excise_fields.scss';
import {COLORS} from "../../../../../../../../../../../../../config/constants";
import {useDemoExciseFields} from "components/cabinet/demo/steps/packaging/use-product-form";

type Props = {
  onClose(): void
  exciselist: Excise[]
  product?: ProductParamsOutput
  isPrefillLoading?: boolean
  prefillSuggestions?: SuggestAdditionalFieldsOutput | null
}

enum EXCISE_FIELDS {
  EXCISE_VALUE = 'excise_value',
  EXCISE_RATE = 'excise_rate',
}

type ExciseFieldsParams = {
  [EXCISE_FIELDS.EXCISE_VALUE]: number
  [EXCISE_FIELDS.EXCISE_RATE]: number
}

const ExciseFields = ({onClose, exciselist, product, isPrefillLoading, prefillSuggestions}: Props) => {

  const { t } = useTranslation()

  const demoPlayback = useDemoExciseFields(prefillSuggestions)

  const {
    setValue: setValuePackageForm,
    trigger: triggerPackageForm,
    getValues: getValuesPackageForm,
    watch: watchPackageForm,
  } = useFormContext<PackageForm>()

  const engineType = watchPackageForm(PACKAGE_FORM.ENGINE_TYPE);
  const enginePowerHp = watchPackageForm(PACKAGE_FORM.ENGINE_POWER_HP);
  const engineVolumeCc = watchPackageForm(PACKAGE_FORM.ENGINE_VOLUME_CC);
  const manufactureDate = watchPackageForm(PACKAGE_FORM.MANUFACTURE_DATE);

  const userExciseValue = product?.[EXCISE_FIELDS.EXCISE_VALUE] === 0 ? 0 : product?.[EXCISE_FIELDS.EXCISE_VALUE] || getValuesPackageForm(PACKAGE_FORM.EXCISE_VALUE);

  const resolveSavedExciseRate = () => {
    const fromProduct = product?.[EXCISE_FIELDS.EXCISE_RATE];
    const fromForm = getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE);

    return {
      description:
        fromProduct?.description ??
        fromForm?.Condition ??
        // @ts-ignore
        fromForm?.description,
      value:
        fromProduct?.value?.toString() ??
        fromForm?.Value?.toString() ??
        // @ts-ignore
        fromForm?.value?.toString(),
    };
  };

  const currentExciseRate = exciselist.find((excise) => {
    const { description, value } = resolveSavedExciseRate();

    if (description && excise.Condition === description) {
      return true;
    }

    // У нескольких ставок Value может быть «0» — сопоставляем только по Condition.
    if (value && value !== '0' && excise.Value === value) {
      return true;
    }

    return false;
  });

  const currentExciseRateIndex = () => findIndex(exciselist, currentExciseRate);

  const [ exciseRateByUser, setExciseByUserRate ] = useState((currentExciseRate && currentExciseRateIndex() !== -1) ? currentExciseRateIndex() : "");
  const [ measure, setMeasure ] = useState((currentExciseRate && currentExciseRateIndex() !== -1) ? currentExciseRate?.Measure.Razm : "");
  const [ exciseValue, setExciseValue ] = useState(currentExciseRate?.Value || "");

  const dispatch = useDispatch()

  const exciseValueIsZero = exciseValue === "0";

  const hasExcise = exciselist?.length > 0;

  const isSingleValue = exciselist?.length === 1;

  const isVehicle = isVehicleHsCode(getValuesPackageForm(PACKAGE_FORM.CODE) || product?.code);

  const { register, getValues, setValue, trigger, resetField, watch, formState: { errors } } = useForm<ExciseFieldsParams>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      [EXCISE_FIELDS.EXCISE_VALUE]: (userExciseValue || userExciseValue === 0) ? Number(userExciseValue): undefined,
      [EXCISE_FIELDS.EXCISE_RATE]: currentExciseRateIndex()
    }
  })

  watch(EXCISE_FIELDS.EXCISE_VALUE);

  const [ openAlert, setOpenAlert ] = React.useState(false);

  const handleCloseAlert = () => setOpenAlert(false);

  useEffect(() => {
    if (!prefillSuggestions || isPrefillLoading) {
      return;
    }

    if (prefillSuggestions.excise_rate_index !== undefined) {
      if (demoPlayback.shouldApplyExciseRateIndex) {
        const excise = exciselist[prefillSuggestions.excise_rate_index];
        if (excise) {
          setExciseByUserRate(prefillSuggestions.excise_rate_index);
          setExciseValue(excise.Value);
          setMeasure(excise.Measure.Razm);
          setValue(EXCISE_FIELDS.EXCISE_RATE, prefillSuggestions.excise_rate_index);
          setValuePackageForm(PACKAGE_FORM.EXCISE_RATE, excise);
        }
      }
    } else if (prefillSuggestions.excise_rate) {
      const exciseIndex = findIndex(
        exciselist,
        (excise) => excise.Condition === prefillSuggestions.excise_rate?.description,
      );
      if (exciseIndex !== -1) {
        const excise = exciselist[exciseIndex];
        setExciseByUserRate(exciseIndex);
        setExciseValue(excise.Value);
        setMeasure(excise.Measure.Razm);
        setValue(EXCISE_FIELDS.EXCISE_RATE, exciseIndex);
        setValuePackageForm(PACKAGE_FORM.EXCISE_RATE, excise);
      }
    }

    if (prefillSuggestions.excise_value !== undefined) {
      setValue(EXCISE_FIELDS.EXCISE_VALUE, Number(prefillSuggestions.excise_value));
    }
  }, [ prefillSuggestions, isPrefillLoading, exciselist, demoPlayback.shouldApplyExciseRateIndex, setValue, setValuePackageForm ]);

  const checkVehicleHasErrors = () => {
    if (!isVehicle) {
      return false;
    }
    const needsVolume = engineType !== 'electric' && !engineVolumeCc;
    return !engineType || !enginePowerHp || !manufactureDate || needsVolume;
  }

  const checkHasErrors = () => {
    if (checkVehicleHasErrors()) {
      return true;
    }
    if (!hasExcise || exciseValueIsZero) {
      return false;
    }
    // Для авто количество для акциза = мощность (л.с.), отдельное поле не заполняется
    if (isVehicle) {
      return !isSingleValue && exciseRateByUser === "";
    }
    const exciseValueHasErrors = Boolean(!getValues()[EXCISE_FIELDS.EXCISE_VALUE] || Boolean(errors[EXCISE_FIELDS.EXCISE_VALUE]));
    return isSingleValue ? exciseValueHasErrors : exciseValueHasErrors || exciseRateByUser === "";
  }

  const onSubmit = async () => {

    if(hasExcise && !isSingleValue && typeof exciseRateByUser === "string") {
      return
    }

    let exciseRate;
    if (hasExcise) {
      exciseRate = isSingleValue ? exciselist[0] : exciselist[Number(exciseRateByUser)];
    }
    // Для авто мощность (л.с.) — это и есть количество для акциза
    const exciseValue = isVehicle
      ? getValuesPackageForm(PACKAGE_FORM.ENGINE_POWER_HP)
      : getValues(EXCISE_FIELDS.EXCISE_VALUE);

    const vehicleFields = isVehicle ? getVehicleFields(getValuesPackageForm()) : {};

    if (product) {
      const data = {
        ...product,
        ...demoPlayback.getProductFormValues(getValuesPackageForm()),
        ...vehicleFields,
        ...(exciseRate && {
          [EXCISE_FIELDS.EXCISE_VALUE]: exciseValue,
          [EXCISE_FIELDS.EXCISE_RATE]: {
            description: exciseRate.Condition,
            document: exciseRate.Document,
            unit: exciseRate.Measure.Razm,
            value: exciseRate.Value,
          }
        }),
      };

      // @ts-ignore
      await dispatch(useUpdateProductById(data));
    }

    if (isVehicle) {
      Object.entries(vehicleFields).forEach(([ field, value ]) => {
        // @ts-ignore
        setValuePackageForm(field, value ?? undefined);
      });
    }

    if (exciseRate) {
      // @ts-ignore
      setValuePackageForm(PACKAGE_FORM.EXCISE_VALUE, exciseValue);
      setValuePackageForm(PACKAGE_FORM.EXCISE_RATE, exciseRate);
    }
    triggerPackageForm(PACKAGE_FORM.PALLETES);
    setOpenAlert(true);
    onClose();
  }

  return <><div
    className='packaging-additional'
    {...demoPlayback.additionalFieldsProps}
  >
    {isPrefillLoading && (
      <div className='packaging-additional__loading'>
        <CircularProgress size={32} />
        <Typography variant='body2' sx={{ marginTop: 2 }}>
          {t('actions__message__loadingAdditionalFields')}
        </Typography>
      </div>
    )}
    {isVehicle && <VehicleFields />}
    {hasExcise && <>
      {isSingleValue ? <>{exciselist[0].Value} {exciselist[0].Condition} <br/>
        <Link href={exciselist[0].Link} target='_blank'>{exciselist[0].Document || ''}</Link>
      </> : <>
        <Typography variant='subtitle2' sx={{ fontSize: 16, marginBottom: 2 }}>{t('actions__message__selectValue')}</Typography>
        <FormControl>
          <RadioGroup
            {...demoPlayback.radioGroupProps}
            defaultValue={currentExciseRate ? currentExciseRateIndex() : ""}
            value={exciseRateByUser}
            onChange={({ target: { value: rate } }) => {
              const exciseValue = exciselist[Number(rate)];

              setExciseValue(exciseValue.Value)
              setMeasure(exciseValue.Measure.Razm)
              setExciseByUserRate(Number(rate));
              setValue(EXCISE_FIELDS.EXCISE_RATE, Number(rate))

              if (exciseValue.Value === '0') {
                // @ts-ignore
                resetField(EXCISE_FIELDS.EXCISE_VALUE, { defaultValue: '' });
              }
            }}
            sx={{ fontSize: 14 }}
          >
            {exciselist.map((excise, index) => <FormControlLabel
              key={index}
              sx={{ marginLeft: 0 }}
              value={Number(index)}
              control={<Radio />}
              className='packaging-additional__label'
              {...demoPlayback.getRadioProps(index)}
              label={<>{excise.Value} {excise.Condition}</>
              }/>)}
          </RadioGroup>
        </FormControl>
      </>}
      <Typography variant='body2'>
        <Link href={exciselist[0].Link} target='_blank' sx={{ marginTop: 1, display: 'block' }}>
          {exciselist[0].Document || ''}
        </Link>
      </Typography>
    </>}
    <div className='packaging-additional__fields'>
      {hasExcise && !exciseValueIsZero && !isVehicle && (
        <TextField
          className='packaging-additional__addField'
          variant='outlined'
          {...register(EXCISE_FIELDS.EXCISE_VALUE, { required: !exciseValueIsZero, validate: (value: number) => checkFloat(Number(value), t('errors__message__incorrectValue')), min: { value: 0, message: t('errors__message__incorrectValue')} })}
          onChange={(e) => {
            // @ts-ignore
            setValue(EXCISE_FIELDS.EXCISE_VALUE, e.target.value);
            trigger(EXCISE_FIELDS.EXCISE_VALUE);
          }}
          error={Boolean(errors[EXCISE_FIELDS.EXCISE_VALUE])}
          label={exciselist[0].Measure.Name}
          InputLabelProps={{ shrink: true }}
          onBlur={async () => await trigger([ EXCISE_FIELDS.EXCISE_RATE ])}
          InputProps={{
            endAdornment: measure
          }}
          sx={{
            '.MuiOutlinedInput-root':{
              color: COLORS.LIGHT_TEXT_SECONDARY
            }
          }}
        />
      )}
      <Button
        className='packaging-additional__cancelButton'
        variant='outlined'
        sx={{ minWidth: '196px'}}
        onClick={onClose}
        disabled={isPrefillLoading}
      >{t('actions__text__cancel')}</Button>
      <Button
        {...demoPlayback.saveButtonProps}
        className='packaging-additional__closeButton'
        variant='contained'
        fullWidth
        onClick={onSubmit}
        startIcon={<CheckIcon />}
        disabled={isPrefillLoading || checkHasErrors()}
      >{t('actions__text__save')}</Button>
    </div>
  </div>
  <AlertBottom
    open={openAlert}
    onClose={handleCloseAlert}
    message={t('actions__message__codeSuccess')}
  />
  </>
}

export default ExciseFields
